import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import { useAvivaSelector } from "@redux/hook";
import React from "react";
import { useTheme, ITheme, themeDefault } from "src/theme-revamp";
import { PLANS_NAME } from "@static/config";
import CalculatingLoader from "@components/_Revamp/CalculatingLoader";
import Bullet from "./Bullet";
import DOMPurify from 'dompurify'

const ListingLayout = styled.div<Partial<ListingProps>>`
  margin: ${(props) => (props.margin ? props.margin : "0")};
  height: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  overflow: -moz-scrollbars-none;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
  @media screen and (max-height: 768px) {
    max-height: ${(props) => (props.maxHeight ? props.maxHeight : "70vh")};
  }
`;

const ListingItemDescriptionContainer = styled.div<{ gap?: string }>`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  @media screen and (min-width: 768px) {
    row-gap: ${(props) => props.gap || "26px"};
  }
`;

const ListingDivider = styled.div<DividerProps>`
  background-color: ${(props) =>
    props.dividerBackground ? props.dividerBackground : "#C4C4C4"};
  height: 1px;
  margin-top: ${(props) =>
    props.dividerMarginTop ? props.dividerMarginTop : "48px"};
  margin-bottom: ${(props) =>
    props.dividerMarginBottom ? props.dividerMarginBottom : "48px"};
`;

const SummaryText = styled.h4<SummaryTextProps>`
  color: ${(props) =>
    props.summaryTextColor ? props.summaryTextColor : "#883e89"};
  font-size: ${(props) => (props.fontSize ? props.fontSize : "20px")};
  font-weight: 600;
  line-height: 1.4;
`;

const ListingDescriptionText = styled.p<{ width?: string; fontSize?: string }>`
  color: #434343;
  font-size: ${(props) => props.fontSize || "14px"};
  font-weight: 500;
  line-height: 1.4;
  width: ${(props) => (props.width ? props.width : "50%")};
  max-width: 350px;
  a {
    color: #883e89;
    font-weight: 600;
    cursor: pointer;
  }
`;

const ListingDescriptionValue = styled.p<
  Pick<ListingProps, "variant"> & { fontSize?: string }
>`
  color: #434343;
  font-size: ${(props) => props.fontSize || "16px"};
  font-weight: ${(props) =>
    props.variant === "summarization" ? "600" : "400"};
  line-height: 1.4;
  text-align: right;
`;

const ListingItemContainer = styled.div<
  Pick<ListingProps, "variant" | "underlineColor"> & {
    isShieldStarter?: boolean;
    isAuto?: boolean;
  }
>`
  display: flex;
  flex-direction: column;
  row-gap: ${(props) =>
    props.isShieldStarter
      ? "3px"
      : props.variant === "summarization"
      ? "16px"
      : "3px"};
  padding: ${(props) => (props.variant === "alternate" ? "24px" : "0")};
  height: ${(props) => (props.isAuto ? "auto" : "48px")};
  border-bottom: ${(props) =>
    props.variant === "alternate"
      ? "1px solid" + (props.underlineColor ? props.underlineColor : "#C4C4C4")
      : "none"};
  &:nth-last-child(1) {
    border-bottom: none;
  }
`;

const ListingHeadingText = styled.p<
  Pick<ListingProps, "variant" | "headingFontSize">
>`
  color: #434343;
  font-size: ${(props) =>
    props.headingFontSize
      ? props.headingFontSize
      : props.variant === "summarization"
      ? "20px"
      : "16px"};
  font-weight: 600;
  line-height: 1.4;
  margin-right: 20px;
  flex: 1;
  @media screen and (max-width: 768px) {
  }
`;

const ListingHeadingValue = styled.div<
  Pick<
    ListingProps,
    "variant" | "headerMaxValueWidth" | "listingHeadingFontSize"
  >
>`
  position: relative;
  color: #434343;
  font-size: ${(props) =>
    props.listingHeadingFontSize ? props.listingHeadingFontSize : "16px"};
  font-weight: ${(props) => (props.variant === "alternate" ? "400" : "600")};
  line-height: 1.4;
  text-align: right;
  flex: 0 0
    ${(props) =>
      props.headerMaxValueWidth ? props.headerMaxValueWidth : "140px"};

  @media screen and (min-width: 768px) {
    flex: 1;
    font-size: ${(props) =>
      props.listingHeadingFontSize ? props.listingHeadingFontSize : "16px"};
    flex: 0 0
      ${(props) =>
        props.headerMaxValueWidth ? props.headerMaxValueWidth : "333px"};
  }
`;

const ListingLineContainer = styled.div<
  Pick<ListingProps, "headingColor"> & { alignItems?: string; gap?: string }
>`
  display: flex;
  align-items: ${(props) => (props.alignItems ? props.alignItems : "center")};
  justify-content: space-between;
  gap: ${(props) => props.gap || "24px"};
  ${ListingHeadingText} {
    color: ${(props) => (props.headingColor ? props.headingColor : "#434343")};
  }
  ${ListingHeadingValue} {
    color: ${(props) => (props.headingColor ? props.headingColor : "#434343")};
  }
`;

const ListingLineContainerList = styled.div<
  Pick<ListingProps, "headingColor"> & { alignItems?: string; gap?: string }
>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.alignItems ? props.alignItems : "center")};
  justify-content: space-between;
  gap: ${(props) => props.gap || "24px"};
  ${ListingHeadingText} {
    color: ${(props) => (props.headingColor ? props.headingColor : "#434343")};
  }
  ${ListingHeadingValue} {
    color: ${(props) => (props.headingColor ? props.headingColor : "#434343")};
  }
`;

const ListingContainer = styled.div<
  Pick<ListingProps, "variant"> & { customGap?: string }
>`
  display: flex;
  flex-direction: column;
  row-gap: ${(props) =>
    props.customGap
      ? props.customGap
      : props.variant === "default"
      ? "26px"
      : props.variant === "summarization"
      ? "48px"
      : "0"};
  @media screen and (min-width: 768px) {
    row-gap: ${(props) =>
      props.customGap
        ? props.customGap
        : props.variant === "default"
        ? "26px"
        : props.variant === "summarization"
        ? "48px"
        : "0"};
  }
`;

const ListingDisclaimerText = styled.p`
  display: block;
  color: #434343;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  width: 100%;
`;

const LoaderComponentClass = css`
  position: absolute;
  right: 0;
  top: -20px;
`;

const ListingDescriptionClass = css`
  width: 70%;
  margin-bottom: 0;
`;

const ListingSubTotal = styled.div<{ alignItems: string | undefined }>`
  align-items: ${(p) => p.alignItems || "flex-start"};
  display: flex;
  justify-content: space-between;
  margin: 15px auto;
  &:last-child {
    margin-top: 15px;
  }
`;

const SubtotalContainer = styled.div`
  margin-top: 10px;
`;

const SubtextContainer = styled.p`
  margin-bottom: 1rem;
`;

export interface Content {
  text: string;
  subtext?: any;
  value?: string;
  isHtml?: boolean;
  bulletVariant?: "default" | "error" | "nobullet";
}

export interface Source {
  heading: Content;
  descriptions?: Content[];
  subDescriptions?: Content[];
}
interface subTotal {
  label: string;
  value: string;
}
export interface ListingProps {
  gap?: string;
  variant: "default" | "summarization" | "alternate";
  subTotal?: subTotal[] | undefined;
  sources: Source[];
  theme?: ITheme;
  margin: string;
  marginItems: string | number;
  underlineColor?: string;
  headingColor?: string;
  summary: Content;
  isCalculating?: boolean;
  alignDescriptionItems?:
    | "stretch"
    | "center"
    | "flex-start"
    | "flex-end"
    | "baseline"
    | "initial"
    | "inherit";
  headerMaxValueWidth?: string;
  summaryTextColor?: string;
  summaryTextFontSize?: string;
  summaryValueFontSize?: string;
  listingHeadingFontSize?: string;
  headingFontSize?: string;
  dividerMarginTop?: string;
  dividerMarginBottom?: string;
  showDisclaimer?: boolean;
  disclaimerText?: string | string[];
  disclaimerVariant?: "top-divider";
  maxHeight?: string;
}

interface SummaryTextProps {
  fontSize?: string;
  summaryTextColor?: string;
}
interface DividerProps {
  dividerMarginTop?: string;
  dividerMarginBottom?: string;
  dividerBackground?: string;
}

function Listing({
  sources,
  subTotal,
  summary,
  variant,
  underlineColor,
  margin,
  marginItems,
  isCalculating,
  headingColor,
  alignDescriptionItems,
  headerMaxValueWidth,
  summaryTextColor,
  headingFontSize,
  summaryTextFontSize,
  summaryValueFontSize,
  listingHeadingFontSize,
  dividerMarginTop,
  dividerMarginBottom,
  showDisclaimer,
  disclaimerText,
  disclaimerVariant,
  maxHeight,
}: ListingProps) {
  const theme = useTheme();
  const CurrentPolicyFlow = useAvivaSelector(
    (state) => state.loading.CurrentPolicyFlow.policy
  );
  const isShieldStarter = [
    PLANS_NAME.SHIELD_STARTER,
    PLANS_NAME.EVER_GREEN,
  ].includes(CurrentPolicyFlow as PLANS_NAME);
  return (
    <ListingLayout margin={margin} theme={theme} maxHeight={maxHeight}>
      <ListingContainer customGap={"0px"} variant={variant}>
        {sources.map((item: Source, i: number) => (
          <ListingItemContainer
            key={i}
            underlineColor={underlineColor}
            variant={variant}
            isShieldStarter={isShieldStarter}
            isAuto={true}
          >
            {item.heading.text && (
              <ListingLineContainer headingColor={headingColor}>
                <ListingHeadingText
                  headingFontSize={headingFontSize}
                  variant={variant}
                >
                  {item.heading.text}
                </ListingHeadingText>
                <ListingHeadingValue
                  headerMaxValueWidth={headerMaxValueWidth}
                  variant={variant}
                  listingHeadingFontSize={listingHeadingFontSize}
                >
                  {isCalculating ? (
                    <CalculatingLoader className={LoaderComponentClass} />
                  ) : (
                    item.heading.value
                  )}
                </ListingHeadingValue>
              </ListingLineContainer>
            )}
            {item.descriptions?.length ? (
              <ListingItemDescriptionContainer
                gap={CurrentPolicyFlow === PLANS_NAME.EVER_GREEN ? "16px" : ""}
              >
                {item.descriptions.map((description: Content, j: number) => (
                  <ListingLineContainer
                    key={j}
                    alignItems={alignDescriptionItems}
                    className=""
                  >
                    <Bullet
                      className={ListingDescriptionClass}
                      fontSize={14}
                      variant={description.bulletVariant || "default"}
                    >
                      <ListingDescriptionText
                        fontSize={isShieldStarter ? "14px" : ""}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize( description.text )}}
                      ></ListingDescriptionText>
                    </Bullet>
                    <ListingDescriptionValue
                      fontSize={isShieldStarter ? "14px" : ""}
                      variant={variant}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(`${description.value || ""}`),
                      }}
                    ></ListingDescriptionValue>
                  </ListingLineContainer>
                ))}
              </ListingItemDescriptionContainer>
            ) : null}
          </ListingItemContainer>
        ))}
      </ListingContainer>
      {subTotal &&
        subTotal.map((items: subTotal, i) => {
          return (
            <SubtotalContainer>
              {i > 0 && <hr />}
              <ListingSubTotal
                key={"subtlt-" + i}
                alignItems={alignDescriptionItems}
              >
                <ListingDescriptionText
                  fontSize={isShieldStarter ? "14px" : ""}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(items.label )}}
                ></ListingDescriptionText>
                <ListingDescriptionValue
                  fontSize={isShieldStarter ? "14px" : ""}
                  variant={variant}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize( items.value || "" )}}
                ></ListingDescriptionValue>
              </ListingSubTotal>
            </SubtotalContainer>
          );
        })}
      {summary && (
        <>
          <ListingDivider
            dividerMarginBottom={dividerMarginBottom}
            dividerMarginTop={dividerMarginTop}
            dividerBackground={themeDefault.colors.grey[500]}
          ></ListingDivider>
          <ListingLineContainer>
            {isShieldStarter ? (
              <SummaryText
                summaryTextColor={summaryTextColor}
                fontSize={summaryTextFontSize}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(summary.text) }}
              />
            ) : (
              <SummaryText
                fontSize={summaryTextFontSize}
                summaryTextColor={summaryTextColor}
              >
                {summary.text}
              </SummaryText>
            )}
            {isShieldStarter ? (
              <SummaryText
                summaryTextColor={summaryTextColor}
                fontSize={summaryValueFontSize}
                dangerouslySetInnerHTML={{ __html:DOMPurify.sanitize( summary?.value || "") }}
              />
            ) : (
              <SummaryText
                fontSize={summaryValueFontSize}
                summaryTextColor={themeDefault.colors.primary}
              >
                {summary.value}
              </SummaryText>
            )}
          </ListingLineContainer>
          {summary.subtext && summary?.isHtml ? (
            <SubtextContainer
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(summary?.subtext || "") }}
            />
          ) : (
            <SubtextContainer>{summary.subtext}</SubtextContainer>
          )}
        </>
      )}
      {showDisclaimer && (
        <>
          {disclaimerVariant === "top-divider" && (
            <ListingDivider
              dividerMarginBottom="20px"
              dividerMarginTop="0"
              dividerBackground={themeDefault.colors.grey[700]}
            ></ListingDivider>
          )}
          <ListingItemContainer
            variant={variant}
            isShieldStarter={isShieldStarter}
          >
            {!Array.isArray(disclaimerText) && (
              <ListingLineContainer>
                <ListingDisclaimerText>
                  {disclaimerText || ""}
                </ListingDisclaimerText>
              </ListingLineContainer>
            )}

            {Array.isArray(disclaimerText) && (
              <ListingLineContainerList>
                {disclaimerText.map((content) => {
                  return (
                    <ListingDisclaimerText>{content}</ListingDisclaimerText>
                  );
                })}
              </ListingLineContainerList>
            )}
          </ListingItemContainer>
        </>
      )}
    </ListingLayout>
  );
}

Listing.defaultProps = {
  variant: "default",
  sources: [],
  margin: "0 0 30px",
  marginItems: 0,
  summary: null,
  underlineColor: "",
};

export default Listing;
